import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

/**
 * Available languages in the application
 * @type {Object.<string, string>}
 */
export const AVAILABLE_LANGUAGES = {
  English: 'English',
  Hindi: 'Hindi'
};

/**
 * Context for managing language state across the application
 * @type {React.Context}
 */
const LanguageContext = createContext(null);

/**
 * Detects the user's preferred language from browser settings
 * Falls back to English if the preferred language is not supported
 * @returns {string} Language code ('en', 'hi')
 */
const detectBrowserLanguage = () => {
  try {
    const browserLang = navigator.language || navigator.userLanguage;
    // Map browser language codes to our display language names
    if (browserLang.startsWith('hi')) {
      return 'Hindi';
    }
    // Default to English for all other languages
    return 'English';
  } catch (error) {
    console.error('Error detecting browser language:', error);
    return 'English'; // Default to English on error
  }
};

/**
 * Provider component for language context
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} Provider component
 */
export const LanguageProvider = ({ children }) => {
  // Get the language from localStorage, browser preference, or default to English
  const [language, setLanguage] = useState(() => {
    try {
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage && Object.keys(AVAILABLE_LANGUAGES).includes(savedLanguage)) {
        return savedLanguage;
      }
      return detectBrowserLanguage();
    } catch (error) {
      console.error('Error initializing language:', error);
      return 'English'; // Default to English on error
    }
  });

  // Update localStorage when language changes
  useEffect(() => {
    try {
      localStorage.setItem('language', language);
      // Update document language attribute for accessibility
      document.documentElement.lang = language;
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  }, [language]);

  /**
   * Function to change the application language
   * @param {string} lang - Language code to change to
   * @returns {boolean} Success status of the language change
   */
  const changeLanguage = useCallback((lang) => {
    if (!lang || !Object.keys(AVAILABLE_LANGUAGES).includes(lang)) {
      console.error(`Invalid language: ${lang}. Must be one of: ${Object.keys(AVAILABLE_LANGUAGES).join(', ')}`);
      return false;
    }
    setLanguage(lang);
    return true;
  }, []);

  // Context value with memoized functions
  const contextValue = {
    language,
    changeLanguage,
    availableLanguages: AVAILABLE_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Custom hook to use the language context
 * @returns {Object} Language context with language code and change function
 * @throws {Error} If used outside of a LanguageProvider
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};